import React, { memo } from 'react';
import { Box, Button, CircularProgress, Typography, Stack, Popover, Skeleton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { useTranslation } from 'react-i18next';
import type { CasinoCategory, CasinoGame } from '../../../Casino/Type/CasinoTypes';
import {
  formatRtp,
  formatVolatility,
  getGameCardImageUrl,
  getAlternativeImageUrl
} from '../../../Casino/CasinoHelper';
import { toImageSrc } from '../../../Utils/ImagePreload';
import styles from '../../../assets/css/components/CasinoLobby.module.css';

// Skeleton card for loading state
export const SkeletonCard = memo(() => (
  <Box>
    <Box sx={{ width: '100%', height: 'auto', aspectRatio: '287 / 193', mb: 0.5 }}><Skeleton variant='rounded' height="100%" /></Box>
    <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
      <Skeleton variant="rounded" width={14} height={14} />
      <Skeleton variant="text" width={60} sx={{ fontSize: '10px' }} />
      <Skeleton variant="circular" width={16} height={16} sx={{ ml: 'auto' }} />
    </Stack>
  </Box>
));

SkeletonCard.displayName = 'SkeletonCard';

// Game Card Props
export interface GameCardProps {
  game: CasinoGame;
  providerName: string | null | undefined;
  providerIconUrl: string | null | undefined;
  gameCategories: CasinoCategory[];
  launchingGameId: string | null;
  onLaunch: (game: CasinoGame, demo: boolean) => void;
  taggedGame: boolean;
}

// Memoized Game Card component for better performance
export const GameCard = memo<GameCardProps>(({
  game,
  providerName,
  providerIconUrl,
  launchingGameId,
  taggedGame,
  onLaunch
}) => {
  const { t } = useTranslation('Casino');
  const showProviderBadge = Boolean(providerName || providerIconUrl);
  const rtp = formatRtp(game.rtp);
  const volatility = formatVolatility(game.volatility);
  const isLaunching = launchingGameId === game.id;
  const providerIconSrc = toImageSrc(providerIconUrl);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box className={styles.gameCard}>
      <Box className={styles.gameImageWrapper} sx={{ mb: 0.5 }}>
        <img
          src={taggedGame ? getAlternativeImageUrl(game) : getGameCardImageUrl(game)}
          alt={game.title}
          className={styles.gameImage}
          loading="lazy"
          style={{ aspectRatio: taggedGame ? '17 / 19' : '4 / 3' }}
        />
        <Box className={styles.gameImageOverlay}>
          <Stack direction="row" className={styles.gameActions} spacing={1}>
            <Button
              variant="contained"
              size="small"
              disabled={isLaunching}
              onClick={(e) => { e.stopPropagation(); onLaunch(game, false); }}
              className={styles.playButton}
            >
              {isLaunching ? (
                <CircularProgress size={14} sx={{ color: 'white' }} />
              ) : (
                t('gameCard.play')
              )}
            </Button>
            {game.demoWorking === true && (
              <Button
                variant="outlined"
                size="small"
                disabled={isLaunching}
                onClick={(e) => { e.stopPropagation(); onLaunch(game, true); }}
                className={styles.demoButton}
              >
                {t('gameCard.demo')}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
      {showProviderBadge && (
        <Stack direction="row" spacing={1} alignItems="center">
          {providerIconSrc && (
            <img
              src={providerIconSrc}
              alt={providerName || 'Provider'}
              className={styles.providerIcon}
              height={13}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          {providerName && <Typography className={styles.providerText}>{providerName}</Typography>}
          <Box
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            sx={{ display: 'inline-flex', cursor: 'pointer', ml: 'auto !important' }}
          >
            <InfoOutlinedIcon className={styles.infoIcon} />
          </Box>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            disableRestoreFocus
            disableAutoFocus
            disableEnforceFocus
            sx={{
              pointerEvents: 'none',
            }}
            slotProps={{
              paper: {
                tabIndex: -1,
              },
            }}
          >
            <Stack direction="row" spacing={1} className={styles.gameInfoPopover}>
              <Stack direction="column">
                <Typography className={styles.gameInfoPopoverTitle}>{t('gameCard.volatility')}</Typography>
                <Box className={styles.gameInfoPopoverBox}>
                {game.volatility === null || game.volatility === undefined || game.volatility === 0 ? (
                    <QuestionMarkIcon className={styles.gameInfoPopoverIcon}  sx={{ color: '#9A9AA0'}}/>
                  ) : (
                    <Typography className={styles.gameInfoPopoverValue}>{volatility}</Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="column">
                <Typography className={styles.gameInfoPopoverTitle}>{t('gameCard.rtp')}</Typography>
                <Box className={styles.gameInfoPopoverBox}>
                  {game.rtp === null || game.rtp === undefined || game.rtp === 0 ? (
                    <QuestionMarkIcon className={styles.gameInfoPopoverIcon}  sx={{ color: '#9A9AA0'}}/>
                  ) : (
                    <Typography className={styles.gameInfoPopoverValue}>{rtp}</Typography>
                  )}
                </Box>
              </Stack>
              {game.isBonusBuy === true && (
                <Stack direction="column">
                  <Typography className={styles.gameInfoPopoverTitle}>{t('gameCard.bonusBuy')}</Typography>
                  <Box className={styles.gameInfoPopoverBox}>
                    <CheckIcon className={styles.gameInfoPopoverIcon} sx={{ color: '#82c258'}}/>
                  </Box>
                </Stack>
              )}
              {game.isMegaways === true && (
                <Stack direction="column">
                  <Typography className={styles.gameInfoPopoverTitle}>{t('gameCard.megaways')}</Typography>
                  <Box className={styles.gameInfoPopoverBox}>
                    <CheckIcon className={styles.gameInfoPopoverIcon} sx={{ color: '#82c258'}}/>
                  </Box>
                </Stack>
              )}
            </Stack>
          </Popover>
        </Stack>
      )}
      {/* {gameCategories.length > 0 && (
        <Box className={styles.categoryBadges}>
          {gameCategories.slice(0, 3).map((category) => {
            const iconSrc = toImageSrc(getCategoryIconUrl(category));
            if (!iconSrc) return null;
            return (
              <Tooltip
                key={category.id}
                title={<TooltipContent>{category.title}</TooltipContent>}
                placement="top"
                arrow
                componentsProps={{ tooltip: { sx: tooltipSx } }}
              >
                <span className={styles.categoryBadge}>
                  <img
                    src={iconSrc}
                    alt={category.title}
                    className={styles.categoryIcon}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </span>
              </Tooltip>
            );
          })}
        </Box>
      )} */}
    </Box>
  );
});

GameCard.displayName = 'GameCard';
